import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { cx } from "@admin-ui/utils/cx";
import { formatDate } from "@admin-ui/utils/format-date";
import type AdminWorldSourcesIndex from "@travily/types/pages/AdminWorldSourcesIndex";
import { TableBody } from "react-aria-components";

export type SourceHealth = AdminWorldSourcesIndex["sources"][number];
export type ShadowTotal = AdminWorldSourcesIndex["shadow_totals"][number];
export type BacktestResult = AdminWorldSourcesIndex["backtest"][number];

export interface IndexViewProps {
  sources: SourceHealth[];
  shadowTotals: ShadowTotal[];
  backtest: BacktestResult[];
  onIngest?: (sourceKey: string) => void;
  onAdopt?: (destinationId: number) => void;
}

const PERCENT = 100;
// Past this the two writers are not describing the same city, and the row is
// drawn as the disagreement it is.
const SHADOW_ALERT_PCT = 40;

// The lattice, ordered as the licence calculation orders it: how much a licence
// constrains what may be derived from it. A row's colour is that rank, so a
// share-alike feed never reads like a public-domain one at a glance.
const LICENCE_COLOR: Record<string, "gray" | "blue" | "warning" | "error"> = {
  cc0: "gray",
  public_domain: "gray",
  cc_by: "blue",
  odbl: "warning",
  cc_by_sa: "warning",
  proprietary_facts: "error",
  internal: "error",
};

// Trust is the one number on this page that has no units, so it is drawn
// rather than printed: a 0.45 estimate beside a 0.95 measurement is the
// comparison the page is for.
function ReliabilityBar({ source }: { source: SourceHealth }) {
  return (
    <div className="flex items-center gap-2" title={`${source.reliability} reliability`}>
      <div className="h-2.5 w-24 overflow-hidden rounded-sm bg-quaternary">
        <div className="h-full rounded-sm bg-fg-brand-primary" style={{ width: `${source.reliability * PERCENT}%` }} />
      </div>
      <span className="text-tertiary tabular-nums">{source.reliability.toFixed(2)}</span>
    </div>
  );
}

function LastSeen({ source }: { source: SourceHealth }) {
  if (source.last_ingested_at === null) return <span className="font-medium text-error-primary">Never</span>;

  return (
    <span className={cx(source.stale && "font-medium text-error-primary")}>{formatDate(source.last_ingested_at)}</span>
  );
}

function dollars(cents: number | null) {
  if (cents === null) return "—";

  return `$${Math.round(cents / PERCENT).toLocaleString()}`;
}

// Past the guard's own threshold the composed figure can never publish itself,
// so the only way it ever reaches the corpus is somebody accepting it here.
function refused(row: ShadowTotal): boolean {
  return row.delta_pct !== null && Math.abs(row.delta_pct) > SHADOW_ALERT_PCT;
}

// What the corpus publishes beside what the composed derivation concluded. The
// two agree wherever the derivation was published; a row that disagrees is one
// the guards refused, which is what this table is read for.
function ShadowTotals({ rows, onAdopt }: { rows: ShadowTotal[]; onAdopt?: (destinationId: number) => void }) {
  return (
    <TableCard.Root size="sm">
      <Table aria-label="Composed cost shadow">
        <Table.Header>
          <Table.Head id="name" label="Destination" isRowHeader />
          <Table.Head id="published" label="Published" />
          <Table.Head id="composed" label="Composed" />
          <Table.Head id="delta" label="Delta" />
          <Table.Head id="coverage" label="Coverage" />
          <Table.Head id="grade" label="Grade" />
          <Table.Head id="method" label="Rung" />
          <Table.Head id="actions" label="" />
        </Table.Header>
        <TableBody items={rows}>
          {(row: ShadowTotal) => (
            <Table.Row key={row.destination_id} id={row.destination_id}>
              <Table.Cell className="font-medium">{row.name}</Table.Cell>
              <Table.Cell className="tabular-nums">{dollars(row.published_cents)}</Table.Cell>
              <Table.Cell className="tabular-nums">{dollars(row.composed_cents)}</Table.Cell>
              <Table.Cell
                className={cx(
                  "tabular-nums",
                  row.delta_pct !== null &&
                    Math.abs(row.delta_pct) > SHADOW_ALERT_PCT &&
                    "font-medium text-error-primary",
                )}
              >
                {row.delta_pct === null ? "—" : `${row.delta_pct > 0 ? "+" : ""}${row.delta_pct}%`}
              </Table.Cell>
              <Table.Cell className="text-tertiary tabular-nums">
                {row.coverage === null ? "—" : row.coverage.toFixed(2)}
              </Table.Cell>
              <Table.Cell className="text-tertiary">{row.evidence_grade ?? "—"}</Table.Cell>
              <Table.Cell className="text-tertiary">{row.method ?? "—"}</Table.Cell>
              <Table.Cell>
                {refused(row) && (
                  <Button size="sm" color="secondary" onClick={() => onAdopt?.(row.destination_id)}>
                    Adopt composed figure
                  </Button>
                )}
              </Table.Cell>
            </Table.Row>
          )}
        </TableBody>
      </Table>
    </TableCard.Root>
  );
}

// Each rung's error against the city evidence it was denied. Read down a
// category: a lower rung whose MAPE is not worse than the rung above it is a
// ladder in the wrong order.
function Backtest({ rows }: { rows: BacktestResult[] }) {
  return (
    <TableCard.Root size="sm">
      <Table aria-label="Ladder backtest">
        <Table.Header>
          <Table.Head id="rung" label="Rung" isRowHeader />
          <Table.Head id="category" label="Category" />
          <Table.Head id="samples" label="Places" />
          <Table.Head id="mape" label="MAPE" />
          <Table.Head id="band" label="Band coverage" />
        </Table.Header>
        <TableBody items={rows}>
          {(row: BacktestResult) => (
            <Table.Row key={`${row.rung}-${row.category}`} id={`${row.rung}-${row.category}`}>
              <Table.Cell className="font-medium">{row.rung}</Table.Cell>
              <Table.Cell className="text-tertiary">{row.category}</Table.Cell>
              <Table.Cell className="text-tertiary tabular-nums">{row.samples}</Table.Cell>
              <Table.Cell className="tabular-nums">{row.mape.toFixed(1)}%</Table.Cell>
              <Table.Cell className="tabular-nums">{(row.band_coverage * PERCENT).toFixed(0)}%</Table.Cell>
            </Table.Row>
          )}
        </TableBody>
      </Table>
    </TableCard.Root>
  );
}

export function IndexView({ sources, shadowTotals, backtest, onIngest, onAdopt }: IndexViewProps) {
  const silent = sources.filter((source) => source.last_ingested_at === null).length;
  const stale = sources.filter((source) => source.stale).length;

  return (
    <>
      <div className="rounded-xl bg-secondary p-5 ring-1 ring-secondary ring-inset">
        <p className="text-md text-tertiary">
          {sources.length} registered {sources.length === 1 ? "source" : "sources"}. {silent} have never produced
          anything; {stale} are past the shelf life they declare. A source we are allowed to believe and have never
          heard from is a licence somebody read and a pipe nobody built.
        </p>
      </div>

      <TableCard.Root size="sm">
        <Table aria-label="Knowledge sources">
          <Table.Header>
            <Table.Head id="key" label="Source" isRowHeader />
            <Table.Head id="reliability" label="Reliability" />
            <Table.Head id="licence" label="Licence" />
            <Table.Head id="grain" label="Grain" />
            <Table.Head id="cadence" label="Cadence" />
            <Table.Head id="job" label="Producer" />
            <Table.Head id="last_ingested_at" label="Last ingested" />
            <Table.Head id="actions" label="" />
          </Table.Header>
          <TableBody items={sources}>
            {(source: SourceHealth) => (
              <Table.Row key={source.key} id={source.key}>
                <Table.Cell className="font-medium">{source.key}</Table.Cell>
                <Table.Cell>
                  <ReliabilityBar source={source} />
                </Table.Cell>
                <Table.Cell>
                  <Badge color={LICENCE_COLOR[source.licence] ?? "gray"} size="sm">
                    {source.licence}
                  </Badge>
                </Table.Cell>
                <Table.Cell className="text-tertiary">
                  {source.specificity} · {source.geography}
                </Table.Cell>
                <Table.Cell className="text-tertiary">
                  {source.kind} · {source.cadence}
                </Table.Cell>
                <Table.Cell className="text-tertiary">{source.job ?? "—"}</Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <LastSeen source={source} />
                </Table.Cell>
                <Table.Cell>
                  <Button
                    size="sm"
                    color="secondary"
                    isDisabled={source.job === null}
                    onClick={() => onIngest?.(source.key)}
                  >
                    Run now
                  </Button>
                </Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      </TableCard.Root>

      {shadowTotals.length > 0 && (
        <section className="flex flex-col gap-4">
          <h2 className="font-semibold text-lg text-primary">Composed cost shadow</h2>
          <ShadowTotals onAdopt={onAdopt} rows={shadowTotals} />
        </section>
      )}

      {backtest.length > 0 && (
        <section className="flex flex-col gap-4">
          <h2 className="font-semibold text-lg text-primary">Ladder backtest</h2>
          <Backtest rows={backtest} />
        </section>
      )}
    </>
  );
}
